// Renders an in-app banner when the service worker has installed a new
// version. The user clicks "Reload" to activate it.
//
// We dynamic-import the registration shim so test environments + builds
// without the PWA plugin (e.g. older nodes) don't crash on a missing
// virtual module.
import { useEffect, useState } from 'react';
import { App, Button, Space } from 'antd';
import { isNative } from '@core/platform';

export default function SwUpdatePrompt() {
  const { notification } = App.useApp();
  const [hooks, setHooks] = useState(null);

  useEffect(() => {
    // Native builds ship without the PWA plugin and update through the app
    // stores — there is no service worker to prompt about. See ADR 0004.
    if (isNative()) return undefined;

    let cancelled = false;
    import('virtual:pwa-register/react')
      .then((mod) => {
        if (cancelled) return;
        setHooks({ useRegisterSW: mod.useRegisterSW });
      })
      .catch(() => {
        // No PWA plugin in this build — silently no-op.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!hooks) return null;
  return <SwUpdateInner useRegisterSW={hooks.useRegisterSW} notification={notification} />;
}

function SwUpdateInner({ useRegisterSW, notification }) {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(err) {
      if (import.meta.env.DEV) console.warn('SW registration failed:', err);
    },
  });

  useEffect(() => {
    if (!needRefresh) return;
    const key = `sw-update-${Date.now()}`;
    notification.open({
      key,
      message: 'Update available',
      description: 'A new version of the storefront is ready.',
      duration: 0,
      btn: (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setNeedRefresh(false);
              notification.destroy(key);
            }}
          >
            Later
          </Button>
          <Button type="primary" size="small" onClick={() => updateServiceWorker(true)}>
            Reload
          </Button>
        </Space>
      ),
    });
  }, [needRefresh, notification, setNeedRefresh, updateServiceWorker]);

  return null;
}
